import React from 'react';
import {
  useLoaderData,
  Await,
  useFetcher,
  useLocation,
  useMatches,
  useNavigation,
} from '@remix-run/react';
import type {Product as ProductType} from '@shopify/hydrogen/storefront-api-types';
import {useIsHydrated} from '~/hooks/useIsHydrated';
import * as R from 'ramda';
import {isTWSite} from '~/domains/sanity';
import clsx from 'clsx';

//domains
import {loader} from '~/routes/($lang)/collections/$collectionHandle';
import type {BFSProductType, BFSProductVariant} from '~/domains/collections';
import {CurrencySettingsGlobal, formatCurrency} from 'domains/currency';

//components
import Product from 'components/product/Product';
import CollectionInfinitiyScroll from './CollectionInfinitiyScroll';
import CollectionAds from './CollectionAds';

export default function CollectionProducts() {
  const {
    products,
    totalProducts,
    collectionAdvertisement,
    defaultAdvertisement,
    hasAds,
  } = useLoaderData<typeof loader>();
  const [root] = useMatches();
  const currency = root.data.currency as CurrencySettingsGlobal;
  // const de
  const fetcher = useFetcher<typeof loader>();

  const location = useLocation();
  const params = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const collectionAds = collectionAdvertisement?.advertisement
    ? collectionAdvertisement
    : defaultAdvertisement;

  const [allProducts, setAllProducts] = React.useState<ProductType[] | []>(
    products
      ? transformProducts(
          products,
          currency.hkExchangeRates,
          currency.selectedCurrency,
        )
      : [],
  );
  const [currentPage, setCurrentPage] = React.useState(1);

  const isHydrated = useIsHydrated();
  const navigation = useNavigation();
  // Same-page (filter/sort/page) navigation in flight: keep the old grid
  // visible but dimmed so the update reads as instant instead of a reload.
  const isUpdating =
    navigation.state === 'loading' &&
    navigation.location?.pathname === location.pathname;

  React.useEffect(() => {
    if (!fetcher.data || fetcher.state === 'loading') {
      return;
    }
    if (fetcher.data) {
      const newProducts = fetcher.data.products;
      setAllProducts((prev) => {
        return [
          ...prev,
          ...transformProducts(
            newProducts,
            currency.hkExchangeRates,
            currency.selectedCurrency,
          ),
        ];
      });
    }
  }, [currentPage, fetcher.data, currency]);

  React.useEffect(() => {
    products &&
      setAllProducts(
        transformProducts(
          products,
          currency.hkExchangeRates,
          currency.selectedCurrency,
        ),
      );
    // The grid restarts from the loader's page when the URL changes; without
    // this the infinite-scroll round counter keeps its old value and the next
    // scroll fetch skips every page in between.
    setCurrentPage(1);
  }, [params, location.pathname, currency]);

  const fetchProducts = (query: string) => fetcher.load(query);

  if (totalProducts === 0) {
    return <>No products found</>;
  }

  return (
    <>
      <div
        className={clsx(
          'grid grid-cols-2 gap-x-5 gap-y-6 md:grid-cols-3 lg:grid-cols-4 lg:gap-y-10 lg:pt-0 pb-20',
          location.pathname === '/search' || location.pathname === '/en/search'
            ? 'pt-0'
            : 'pt-4 lg:pt-0',
          'transition-opacity duration-200',
          isUpdating && 'opacity-50 pointer-events-none',
        )}
      >
        <React.Suspense fallback="loading">
          <Await resolve={allProducts}>
            {(products) => {
              if (!products) return <>No products found</>;
              return (
                products &&
                !R.isEmpty(products) &&
                products.map((p, i) => {
                  if (p) {
                    if (i === 1 && hasAds) {
                      return (
                        <>
                          <CollectionAds
                            {...collectionAds.advertisement}
                            className="md:hidden"
                          />
                          <Product key={p.id} product={p} quickAdd />
                        </>
                      );
                    }
                    if (i === 2 && hasAds) {
                      return (
                        <>
                          <CollectionAds
                            {...collectionAds.advertisement}
                            className="hidden md:block lg:hidden"
                          />
                          <Product key={p.id} product={p} quickAdd />
                        </>
                      );
                    }
                    if (i === 3 && hasAds) {
                      return (
                        <>
                          <CollectionAds
                            {...collectionAds.advertisement}
                            className="hidden lg:block"
                          />
                          <Product key={p.id} product={p} quickAdd />
                        </>
                      );
                    }
                    return <Product key={p.id} product={p} quickAdd />;
                  }

                  return <></>;
                })
              );
            }}
          </Await>
        </React.Suspense>
      </div>
      {isHydrated && totalProducts > 0 && (
        <CollectionInfinitiyScroll
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalProducts={totalProducts}
          fetchProducts={fetchProducts}
          hasAds={hasAds}
        />
      )}
    </>
  );
}

function transformProducts(
  products: BFSProductType[],
  hkExchangeRates: CurrencySettingsGlobal['hkExchangeRates'],
  selectedCurrency: CurrencySettingsGlobal['selectedCurrency'],
) {
  return (
    products &&
    (products.map((p: BFSProductType) => {
      // if (p.id === 8648580399357) {
      //   console.log(p);
      // }
      // p.title === 'SHOEI NEOTEC II JAUNT' &&
      //   console.log(p);
      return {
        id: `gid://shopify/Product/${p?.id}`,
        title: p.title,
        availableForSale: p.available,
        publishedAt: p.published_at,
        handle: p.handle,
        tags: p.tags,
        // images: {
        //   nodes: p?.images_info?.map((image) => ({
        //     ...image,
        //     altText: image.alt,
        //   })),
        // },
        variants: {
          nodes: p.variants.map((variant: BFSProductVariant) => {
            const price =
              typeof window !== 'undefined' && isTWSite(window.location.href)
                ? variant?.price_twd
                  ? {
                      amount: variant.price_twd,
                      currencyCode: 'TWD',
                    }
                  : selectedCurrency === 'HKD'
                  ? {
                      amount: variant.price,
                      currencyCode: 'HKD',
                    }
                  : {
                      amount: (
                        parseFloat(variant.price) *
                        hkExchangeRates[selectedCurrency]
                      ).toFixed(2),
                      currencyCode: selectedCurrency,
                    }
                : {
                    amount: variant.price_hkd ?? variant.price,
                    currencyCode: 'HKD',
                  };
            const compareAtPrice =
              typeof window !== 'undefined' && isTWSite(window.location.href)
                ? variant?.compare_at_price_twd
                  ? {
                      amount: variant.compare_at_price_twd,
                      currencyCode: 'TWD',
                    }
                  : selectedCurrency === 'HKD'
                  ? {
                      amount: variant.compare_at_price,
                      currencyCode: 'HKD',
                    }
                  : {
                      amount: (
                        parseFloat(variant.compare_at_price as string) *
                        hkExchangeRates[selectedCurrency]
                      ).toFixed(2),
                      currencyCode: selectedCurrency,
                    }
                : {
                    amount:
                      variant.compare_at_price_hkd ?? variant.compare_at_price,
                    currencyCode: 'HKD',
                  };
            return {
              title: variant.title,
              id: `gid://shopify/ProductVariant/${variant?.id}`,
              availableForSale: variant.available,
              image: {
                altText: null,
                height: 500,
                width: 500,
                url: variant.image ? variant.image : p?.images_info[0]?.src,
              },
              price: price ?? null,
              compareAtPrice: compareAtPrice ?? null,
              product: {
                title: p.title,
                handle: p.handle,
              },
              selectedOptions: variant.merged_options.map((option) => {
                const optionName = option.split(':')[0];
                const optionNameCap =
                  optionName[0].toUpperCase() + optionName.substring(1);
                return {
                  name: optionNameCap,
                  value: option.split(':')[1],
                };
              }),
            };
          }),
        },
        options: p?.options_with_values?.map((v) => {
          return {
            name: v.label,
            values: v?.values?.map((value) => value.title),
          };
        }),
      };
    }) as ProductType[])
  );
}
